import { MediaCard } from "@/types/dto/common/media-card-section";
import { Box } from "@mui/material";
import React from "react";
import WistiaVideo from "@/components/shared/wistia-video";
import { resolveLandingPageAnchorTextColor } from "@/utils/landing-page-background";
import { SectionHeader } from "../section-header";
import bgColorMap from "../media-text-card/constants";

export interface WistiaCenteredSectionProps {
  mediaCardWithText: MediaCard;
  moduleRankingPosition?: string;
}

const WistiaCenteredSection = ({
  mediaCardWithText,
  moduleRankingPosition
}: WistiaCenteredSectionProps) => {
  const isWistiaVideo = (media: any): media is { videoId: string; title: string } =>
    media && typeof media.videoId === "string" && media.videoId.length > 0;

  const resolvedBgColor = bgColorMap[mediaCardWithText.backgroundColor || "Transparent"];

  const isPrimaryBg = resolvedBgColor === "primary.main";
  const anchorTextColor = resolveLandingPageAnchorTextColor(mediaCardWithText.backgroundColor);

  return (
    <Box
      component="section"
      className="fluid-box"
      data-module-name="Wistia Centered Video Section"
      data-parent-container-type="wistia-centered-section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
      data-track="visibility"
      data-testid="wistia-centered-section"
      sx={{
        backgroundColor: resolvedBgColor
      }}
    >
      <Box
        className="full-width-container"
        data-testid="wistia-centered-section-container"
        sx={{
          padding: { xs: "4rem 2.4rem", lg: "6rem 10rem" },
          display: "flex",
          flexDirection: "column",
          gap: "4rem",
          alignItems: "center"
        }}
      >
        <SectionHeader
          headerText={mediaCardWithText.overlayText?.headerText}
          headerLevel="h2"
          headerTestId="wistia-centered-section-header"
          copyTestId="wistia-centered-section-copy"
          sx={{ maxWidth: "100rem", width: "100%", alignSelf: "center", px: { md: "10rem" } }}
          headerSx={{
            fontSize: { lg: "3.2rem", md: "2.8rem", xs: "2.6rem" },
            textTransform: "none",
            textAlign: "center",
            ...(isPrimaryBg && { color: "primary.contrastText" })
          }}
          copyText={mediaCardWithText.overlayText?.subtitle}
          copySx={{
            fontSize: { xs: "1.5rem", md: "1.8rem" },
            textAlign: "center",
            "& a": { color: anchorTextColor, textDecoration: "underline !important" },
            ...(isPrimaryBg && { color: "primary.contrastText" })
          }}
        />
        {mediaCardWithText.media && isWistiaVideo(mediaCardWithText.media) && (
          <Box
            sx={{
              maxWidth: "100rem",
              width: "100%"
            }}
          >
            <WistiaVideo
              videoId={mediaCardWithText.media.videoId}
              title={mediaCardWithText.media.title}
              deferUntilInteraction
              sx={{ borderRadius: "2rem" }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WistiaCenteredSection;
